import { useMachine } from "@xstate/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useOnboardingLoading } from "../../hooks/useOnboardingLoading";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import { getOnboardingConfig } from "../../lib/onboarding/flowConfig";
import {
	FALLBACK_ONBOARDING_CONFIG,
	type OnboardingConfig,
} from "../../lib/onboarding/flowConfig.fallback";
import {
	toEnum,
	toEnumArray,
	toLabel,
	toLabelArray,
} from "../../lib/onboarding/onboardingVocabulary";
import { allQuestions } from "../../lib/onboarding/questionDefinitions";
import { deriveRoleOptions } from "../../machines/onboarding/deriveOptions";
import {
	isDonor,
	shouldSkipCycle,
	shouldSkipJourney,
	shouldSkipRole,
	shouldSkipSteps,
} from "../../machines/onboarding/flowGuards";
import {
	onboardingMachine,
	toFlowAnswers,
} from "../../machines/onboarding/onboardingMachine";
import * as QuestionService from "../../services/questionService";
import { validateQuestionCompletion } from "../../services/questionValidationService";
import type {
	FertilityStatusEnum,
	GenderEnum,
	OnboardingAnswers,
	OnboardingSegmentConfig,
	OnboardingWizardProps,
	Question,
	UserData,
} from "../../types/onboarding";
import { validateBirthYear } from "../../utils/dateValidation";
import { useGlobalLoadingWhile } from "../loading/global-loading-context";
import { OnboardingFooter } from "./OnboardingFooter";
import { OnboardingHeader } from "./OnboardingHeader";
import { OnboardingHeadingCard } from "./OnboardingHeadingCard";
import styles from "./OnboardingWizard.module.css";
import { persistScreen } from "./onboardingSave";
import { ProgressBar } from "./ProgressBar";
import { QuestionRenderer } from "./QuestionRenderer";

// Finds an existing question definition by its rendered `type`.
function questionByType(type: Question["type"]): Question {
	const found = allQuestions.find((q) => q.type === type);
	if (!found) {
		throw new Error(`Missing question definition for type "${type}"`);
	}
	return found;
}

// Machine screen id → the descriptor (Question) used for title + renderer.
const SCREEN_DESCRIPTORS: Record<string, Question> = {
	setupProfile: questionByType("setup-profile"),
	setupDetails: questionByType("setup-profile-details"),
	status: questionByType("fertility-status"),
	role: questionByType("fertility-role"),
	journey: questionByType("fertility-journey-types"),
	steps: {
		id: "journeySteps",
		type: "journey-steps",
		title: "",
		question: "",
		field: "fertilityJourneyTypes",
	},
	cycle: {
		id: "cycle",
		type: "cycle",
		title: "",
		question: "",
		field: "cycleNotRelevant",
	},
	needs: questionByType("needs-most"),
	howDidYouHear: questionByType("how-did-you-hear"),
	additionalInfo: questionByType("additional-info"),
};

const SCREEN_IDS = Object.keys(SCREEN_DESCRIPTORS);
const SCREEN_COUNT = SCREEN_IDS.length;
const SCREEN_INDEX: Record<string, number> = Object.fromEntries(
	SCREEN_IDS.map((id, i) => [id, i]),
);
// Index access (not Array.prototype.at) — `.at()` is unsupported on older mobile
// WebViews (Chrome <92 / iOS <15.4) and Vite doesn't polyfill it.
const LAST_SCREEN_ID = SCREEN_IDS[SCREEN_IDS.length - 1] ?? "";

// Builds the machine's initial answers from persisted user data, mirroring the
// previous useOnboardingLogic seeding: non-empty scalars become strings, arrays
// are kept as-is. The API persists canonical enums but the screens select by
// label, so vocabulary fields are converted enum→label on the way in (toLabel
// passes labels and unknown legacy values through unchanged).
function buildInitialAnswers(userData: UserData | null): OnboardingAnswers {
	const initial: OnboardingAnswers = {};
	if (!userData) return initial;
	for (const [key, value] of Object.entries(userData)) {
		if (value === null || value === undefined || value === "") continue;
		if (Array.isArray(value)) {
			initial[key] = value;
		} else if (typeof value === "boolean") {
			initial[key] = String(value);
		} else {
			initial[key] = String(value);
		}
	}
	if (typeof initial.gender === "string") {
		initial.gender = toLabel("gender", initial.gender);
	}
	if (typeof initial.fertilityStatus === "string") {
		initial.fertilityStatus = toLabel(
			"fertilityStatus",
			initial.fertilityStatus,
		);
	}
	if (typeof initial.fertilityRole === "string") {
		initial.fertilityRole = toLabel("fertilityRole", initial.fertilityRole);
	}
	if (Array.isArray(initial.fertilityJourneyTypes)) {
		initial.fertilityJourneyTypes = toLabelArray(
			"journeyType",
			initial.fertilityJourneyTypes,
		);
	}
	return initial;
}

export function OnboardingWizard({
	onComplete,
	userData,
	initialStepIds,
	segment,
}: OnboardingWizardProps) {
	const [config, setConfig] = useState<OnboardingConfig | null>(null);
	const hasInitialized = useRef(false);

	useEffect(() => {
		if (hasInitialized.current) return;
		hasInitialized.current = true;
		getOnboardingConfig()
			.then(setConfig)
			.catch(() => setConfig(FALLBACK_ONBOARDING_CONFIG));
	}, []);

	// Surface config loading through the single centered GlobalLoadingModal
	// instead of a second, top-aligned loader — otherwise the home→onboarding
	// redirect briefly shows two loaders (the global one + this one).
	useGlobalLoadingWhile(config === null);

	if (config === null) {
		return null;
	}

	return (
		<OnboardingWizardFlow
			config={config}
			userData={userData}
			onComplete={onComplete}
			initialStepIds={initialStepIds}
			segment={segment}
		/>
	);
}

interface OnboardingWizardFlowProps {
	config: OnboardingConfig;
	userData: UserData | null;
	onComplete: (answers: OnboardingAnswers) => Promise<void>;
	initialStepIds?: string[];
	segment?: OnboardingSegmentConfig;
}

// DB-based resume. The user's saved answers come from `/api/user` (works across
// devices, unlike localStorage), so we resume them at the first screen AFTER the
// furthest required screen they've already completed — skipping branch-skipped
// screens. Returns undefined when nothing is completed (brand-new user) so the
// machine starts at screen 1.
//
// "Furthest completed" rather than "first gap" because `journeyCheckedStepIds`
// (the `steps` screen) is persisted in UserStepState, not `/api/user`, so it
// isn't visible here — anchoring on the furthest completed required screen avoids
// wrongly bouncing a returning user back to `steps`. `additionalInfo` is optional
// (always "complete"), so it's never counted as progress evidence.
export function resumeScreen(
	config: OnboardingConfig,
	answers: OnboardingAnswers,
): string | undefined {
	const flow = toFlowAnswers(answers);
	const donor = isDonor(flow);
	const isSkipped = (id: string): boolean => {
		if (id === "role") return shouldSkipRole(config, flow);
		if (id === "journey") return donor || shouldSkipJourney(config, flow);
		if (id === "steps") return donor || shouldSkipSteps(config, flow);
		if (id === "cycle") return donor || shouldSkipCycle(config, flow);
		return false;
	};

	const order = Object.keys(SCREEN_DESCRIPTORS);
	let furthest = -1;
	order.forEach((id, i) => {
		if (id === LAST_SCREEN_ID || isSkipped(id)) return;
		if (computeIsAnswered(SCREEN_DESCRIPTORS[id], answers)) furthest = i;
	});

	if (furthest < 0) return undefined; // nothing completed → start at screen 1

	for (let i = furthest + 1; i < order.length; i++) {
		if (!isSkipped(order[i])) return order[i];
	}
	return LAST_SCREEN_ID;
}

function OnboardingWizardFlow({
	config,
	userData,
	onComplete,
	initialStepIds,
	segment,
}: OnboardingWizardFlowProps) {
	const { t } = useClientTranslations("onboarding");
	const { logOnboarding } = useAnalytics();
	const { isLoading, executeWithLoading } = useOnboardingLoading();

	const initialAnswers = useMemo(() => {
		const base = buildInitialAnswers(userData);
		// journeyCheckedStepIds isn't a /api/user field; seed it from the
		// reconstructed step progress so resume sees the steps screen as done and
		// Back restores prior selections.
		if (initialStepIds && initialStepIds.length > 0) {
			base.journeyCheckedStepIds = initialStepIds;
		}
		return base;
	}, [userData, initialStepIds]);

	// Segment mode (profile editing) starts at a fixed screen with empty back-history
	// so Back from the entry screen closes the overlay; otherwise resume from the
	// furthest completed screen (onboarding).
	const initialScreen = useMemo(
		() =>
			segment ? segment.startScreen : resumeScreen(config, initialAnswers),
		[segment, config, initialAnswers],
	);

	const [snapshot, send] = useMachine(onboardingMachine, {
		input: {
			config,
			initialAnswers,
			initialScreen,
			synthesizeBackHistory: segment ? false : undefined,
		},
	});

	const screenId = String(snapshot.value);
	const answers = snapshot.context.answers;
	const descriptor = SCREEN_DESCRIPTORS[screenId];

	// The terminal "done"/navigating-away state shows through the single centered
	// GlobalLoadingModal rather than a second, top-aligned loader.
	const isTransitioning = screenId === "done" || !descriptor;
	useGlobalLoadingWhile(isTransitioning);

	// Onboarding progress tracking. The flow branches (role/journey/steps/cycle
	// can be skipped), so progress is marked by STABLE step identity + the screen's
	// FIXED position in the spine — never the user's traversal position. A skipped
	// screen simply never fires, surfacing as natural drop-off between consecutive
	// step_order values. Branch props let the funnel be segmented per status/journey.
	// biome-ignore lint/correctness/useExhaustiveDependencies: emit once per screen entry (screenId), not on answer edits within a screen
	useEffect(() => {
		if (screenId === "done" || !SCREEN_DESCRIPTORS[screenId]) return;
		if (segment) return; // profile segment uses profile-scoped analytics, not the onboarding funnel
		const statusEnum = toEnum(
			"fertilityStatus",
			String(answers.fertilityStatus ?? ""),
		);
		const roleEnum = toEnum(
			"fertilityRole",
			String(answers.fertilityRole ?? ""),
		);
		const journeyTypes = Array.isArray(answers.fertilityJourneyTypes)
			? toEnumArray("journeyType", answers.fertilityJourneyTypes)
			: [];
		logOnboarding("screen_viewed", {
			step_id: screenId,
			step_order: SCREEN_INDEX[screenId] ?? -1,
			total_steps: SCREEN_COUNT,
			fertility_status: statusEnum ?? null,
			fertility_role: roleEnum ?? null,
			journey_types: journeyTypes,
			is_donor: isDonor({ fertilityStatus: statusEnum ?? undefined }),
		});
	}, [screenId]);

	const [showValidation, setShowValidation] = useState(false);
	// Clear any validation message when the screen changes.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset must run on screenId change
	useEffect(() => {
		setShowValidation(false);
	}, [screenId]);

	const hasCompleted = useRef(false);
	useEffect(() => {
		if (screenId === "done" && !hasCompleted.current) {
			hasCompleted.current = true;
			void onComplete(snapshot.context.answers);
		}
	}, [screenId, snapshot.context.answers, onComplete]);

	// Segment mode: the journey segment is fully resolved the moment the machine
	// reaches `needs` (the first non-journey screen) — whatever the branch skipped.
	const hasSegmentCompleted = useRef(false);
	useEffect(() => {
		if (!segment || hasSegmentCompleted.current) return;
		if ((SCREEN_INDEX[screenId] ?? -1) >= SCREEN_INDEX.needs) {
			hasSegmentCompleted.current = true;
			// If the entry screen was skipped before the user advanced (history still
			// empty), the segment had nothing to edit — e.g. a row shown per fallback
			// config but skipped per live config. Exit cleanly instead of flashing a
			// spurious "completed".
			if (snapshot.context.history.length === 0) {
				segment.onExit();
			} else {
				segment.onSegmentComplete(snapshot.context.answers);
			}
		}
	}, [
		segment,
		screenId,
		snapshot.context.answers,
		snapshot.context.history.length,
	]);

	const handleInputChange = (
		field: string,
		value: string | number | string[],
	) => {
		send({
			type: "ANSWER",
			field,
			value: typeof value === "number" ? String(value) : value,
		});
	};

	// Inline egg/sperm micro-question on the status screen (donor + non-binary/other).
	const handleDonorTypeSelect = (donorType: "egg" | "sperm") => {
		handleInputChange("donorType", donorType);
		logOnboarding("status_donor_type_selected", { selected_value: donorType });
	};

	const isLastScreen = screenId === LAST_SCREEN_ID;

	const handleNext = async () => {
		try {
			await executeWithLoading(async () => {
				// Persistence + cascade-clear decision lives in the service layer
				// (persistScreen); the component only saves-then-advances.
				await persistScreen(
					screenId,
					snapshot.context.answers,
					snapshot.context.committed,
				);
				send({ type: "NEXT" });
			});
		} catch (error) {
			// Keep the user on-screen; NEXT never fires when a save throws.
			console.error("Error advancing onboarding:", error);
		}
	};

	const handleBack = () => {
		// At the segment entry screen there is no prior screen — Back cancels.
		if (segment && snapshot.context.history.length === 0) {
			segment.onExit();
			return;
		}
		send({ type: "BACK" });
	};

	// Fired when the user taps Continue while the screen's required fields aren't
	// satisfied (button shows its disabled state + inline validation). Logged here
	// rather than in the footer so it can report which fields are still missing.
	const handleShowValidation = () => {
		if (!segment) {
			const missing = missingRequiredFields(descriptor, answers);
			logOnboarding("validation_triggered", {
				step_id: screenId,
				step_order: SCREEN_INDEX[screenId] ?? -1,
				question_type: descriptor.type,
				is_last_screen: isLastScreen,
				...(missing.length > 0 ? { missing_fields: missing } : {}),
			});
		}
		setShowValidation(true);
	};

	// Segment mode: once the journey segment is resolved (machine reached `needs`),
	// onSegmentComplete closes the overlay. Render nothing in the meantime — the
	// container Loader sits at the top of a 100vh flex column and would stack with
	// the global API-call loader the completion refetch triggers (double loader).
	if (segment && (SCREEN_INDEX[screenId] ?? -1) >= SCREEN_INDEX.needs) {
		return null;
	}

	// In segment (profile-edit) mode the wizard renders inside a portal overlay
	// that escapes the body's safe-area padding, so add the status-bar inset here
	// (86ey31vmb — the footer button was pushed off-screen). Normal onboarding
	// gets the inset from the body, so it must NOT be double-applied there.
	const containerClassName = segment
		? `${styles.container} ${styles.containerPortal}`
		: styles.container;

	// Completed view: machine reached the final state. onComplete navigates away
	// immediately; the centered GlobalLoadingModal (driven by the effect above)
	// covers the gap, so render nothing here rather than a second top loader.
	if (isTransitioning) {
		return null;
	}

	const isAnswered = computeIsAnswered(descriptor, answers);
	const showBackButton = screenId !== "setupProfile";

	const genderEnum = toEnum("gender", String(answers.gender ?? ""));
	const statusEnum = toEnum(
		"fertilityStatus",
		String(answers.fertilityStatus ?? ""),
	);
	const showDonorMicroQuestion =
		screenId === "status" &&
		(genderEnum === "non_binary" || genderEnum === "other") &&
		statusEnum === "egg_sperm_donor";

	const statusEnumForExclude =
		toEnum("fertilityStatus", String(answers.fertilityStatus ?? "")) ?? "";
	const excludedJourneyTypes =
		config.flow[statusEnumForExclude as keyof typeof config.flow]?.s7.exclude ??
		[];

	// Per-status role list (incl. the surrogate-only-for-female rule). Passed to
	// the role screen so each status offers its own roles instead of the
	// hardcoded four.
	const roleOptions = deriveRoleOptions(
		config,
		statusEnum as FertilityStatusEnum | undefined,
		genderEnum as GenderEnum | undefined,
	);

	// The journey-steps screen can have a long checklist (e.g. surrogacy). There
	// the list scrolls inside .contentTop and the footer is pinned just below it
	// so the button stays visible without scrolling. Every other screen is short,
	// so the footer renders in-flow right after the content.
	const isLongListScreen = descriptor.type === "journey-steps";

	const footer = (
		<OnboardingFooter
			isAnswered={isAnswered}
			isLoading={isLoading}
			onNext={() => {
				if (!segment) {
					logOnboarding("next_clicked", {
						step_id: screenId,
						step_order: SCREEN_INDEX[screenId] ?? -1,
						is_last_screen: isLastScreen,
					});
				}
				void handleNext();
			}}
			isLastScreen={isLastScreen}
			questionType={descriptor.type}
			answers={answers}
			onInputChange={handleInputChange}
			onShowValidation={handleShowValidation}
			showValidation={showValidation}
		/>
	);

	return (
		<div className={containerClassName}>
			<div className={styles.headerWrapper}>
				<OnboardingHeader
					onBack={handleBack}
					showBackButton={showBackButton}
					showMenuButton={!segment}
				/>
			</div>

			{!segment && (
				<div className={styles.progressBarWrapper}>
					<ProgressBar
						currentIndex={SCREEN_INDEX[screenId] ?? 0}
						total={SCREEN_COUNT}
					/>
				</div>
			)}

			<div className={styles.content}>
				<div className={styles.contentTop}>
					<TitleSection descriptor={descriptor} t={t} />

					<div className={styles.questionContainer}>
						<QuestionRenderer
							question={descriptor}
							answers={answers}
							onInputChange={handleInputChange}
							showValidation={showValidation}
							excludedJourneyTypes={excludedJourneyTypes}
							visibleRoles={roleOptions}
						/>

						{showDonorMicroQuestion && (
							<div className={styles.donorChoice}>
								<p className={styles.subtitle}>{t("donor.eggOrSperm")}</p>
								<div className={styles.donorButtons}>
									<button
										type="button"
										className={`${styles.donorButton} ${
											answers.donorType === "egg"
												? styles.donorButtonSelected
												: ""
										}`}
										onClick={() => handleDonorTypeSelect("egg")}
									>
										{t("donor.egg")}
									</button>
									<button
										type="button"
										className={`${styles.donorButton} ${
											answers.donorType === "sperm"
												? styles.donorButtonSelected
												: ""
										}`}
										onClick={() => handleDonorTypeSelect("sperm")}
									>
										{t("donor.sperm")}
									</button>
								</div>
							</div>
						)}
					</div>

					{/* Short screens: footer flows right after the content. */}
					{!isLongListScreen && footer}
				</div>

				{/* Long-list screens (journey-steps): footer pinned below the
				    scrolling list so it is always visible. */}
				{isLongListScreen && footer}
			</div>
		</div>
	);
}

// Reuses the original per-type title block; adds the two v5 screens.
function TitleSection({
	descriptor,
	t,
}: {
	descriptor: Question;
	t: (key: string) => string;
}) {
	const type = descriptor.type;

	if (type === "setup-profile" || type === "setup-profile-details") {
		return (
			<div className={styles.titleSection}>
				<h1 className={styles.mainTitle}>{t("setupProfile.title")}</h1>
				<p className={styles.subtitle}>{t("setupProfile.subtitle")}</p>
			</div>
		);
	}
	if (type === "fertility-status" || type === "fertility-role") {
		return (
			<h1 className={styles.simpleTitleBlack}>
				{QuestionService.getTranslatedQuestionTitle(descriptor, t)}
			</h1>
		);
	}
	if (type === "fertility-journey-types") {
		return (
			<div className={styles.titleSection}>
				<h1 className={styles.simpleTitleBlack}>
					{t("fertilityJourneyTypes.title")}
				</h1>
				<p className={styles.subtitle}>{t("fertilityJourneyTypes.subtitle")}</p>
			</div>
		);
	}
	if (type === "needs-most") {
		return (
			<div className={styles.titleSection}>
				<h1 className={styles.simpleTitleBlack}>{t("needsMost.title")}</h1>
				<p className={styles.subtitle}>{t("needsMost.subtitle")}</p>
			</div>
		);
	}
	if (type === "how-did-you-hear") {
		return (
			<div className={styles.titleSection}>
				<h1 className={styles.simpleTitleBlack}>{t("howDidYouHear.title")}</h1>
				<p className={styles.subtitle}>{t("howDidYouHear.subtitle")}</p>
			</div>
		);
	}
	if (type === "additional-info") {
		return (
			<div className={styles.titleSection}>
				<h1 className={styles.simpleTitleBlack}>{t("additionalInfo.title")}</h1>
				<p className={styles.subtitle}>{t("additionalInfo.subtitle")}</p>
			</div>
		);
	}
	if (type === "journey-steps") {
		return (
			<div className={styles.titleSection}>
				<h1 className={styles.simpleTitleBlack}>{t("journeySteps.title")}</h1>
				<p className={styles.subtitle}>{t("journeySteps.subtitle")}</p>
			</div>
		);
	}
	if (type === "cycle") {
		return (
			<div className={styles.titleSection}>
				<h1 className={styles.simpleTitleBlack}>{t("cycle.title")}</h1>
				<p className={styles.subtitle}>{t("cycle.subtitle")}</p>
			</div>
		);
	}
	return (
		<OnboardingHeadingCard
			text={QuestionService.getTranslatedQuestionTitle(descriptor, t)}
		/>
	);
}

// Per-screen "answered" check for the footer's enabled state.
// Required fields still empty/invalid for the current screen. Enriches
// `onboarding_validation_triggered` so we can see WHICH field blocked the
// Continue button, not just that the user hit the wall. Extended per screen as
// the onboarding analytics migration proceeds; unknown screens return [] (the
// event still fires, just without per-field detail).
function missingRequiredFields(
	descriptor: Question,
	answers: OnboardingAnswers,
): string[] {
	const missing: string[] = [];
	if (descriptor.type === "setup-profile") {
		if (!String(answers.firstName ?? "").trim()) missing.push("firstName");
	} else if (descriptor.type === "setup-profile-details") {
		const year = String(answers.yearOfBirth ?? "");
		if (!year || !validateBirthYear(year, false).isValid) {
			missing.push("yearOfBirth");
		}
		if (!answers.gender) missing.push("gender");
	} else if (descriptor.type === "fertility-status") {
		if (!answers.fertilityStatus) missing.push("fertilityStatus");
		// Donor + non-binary/other must also pick egg/sperm before advancing.
		const genderEnum = toEnum("gender", String(answers.gender ?? ""));
		const statusEnum = toEnum(
			"fertilityStatus",
			String(answers.fertilityStatus ?? ""),
		);
		if (
			statusEnum === "egg_sperm_donor" &&
			(genderEnum === "non_binary" || genderEnum === "other") &&
			answers.donorType !== "egg" &&
			answers.donorType !== "sperm"
		) {
			missing.push("donorType");
		}
	} else if (descriptor.type === "fertility-role") {
		if (!answers.fertilityRole) missing.push("fertilityRole");
	} else if (descriptor.type === "fertility-journey-types") {
		const journeys = Array.isArray(answers.fertilityJourneyTypes)
			? answers.fertilityJourneyTypes
			: [];
		if (journeys.length === 0) missing.push("fertilityJourneyTypes");
	} else if (descriptor.type === "journey-steps") {
		const checked = Array.isArray(answers.journeyCheckedStepIds)
			? answers.journeyCheckedStepIds
			: [];
		if (checked.length === 0) missing.push("journeyCheckedStepIds");
	} else if (descriptor.type === "cycle") {
		const notRelevant = answers.cycleNotRelevant === "true";
		const hasDate =
			typeof answers.cycleLastPeriodStart === "string" &&
			answers.cycleLastPeriodStart !== "";
		if (!notRelevant && !hasDate) missing.push("cycleLastPeriodStart");
	} else if (descriptor.type === "needs-most") {
		const needs = Array.isArray(answers.needsMost) ? answers.needsMost : [];
		if (needs.length === 0) missing.push("needsMost");
	} else if (descriptor.type === "how-did-you-hear") {
		const referral = Array.isArray(answers.howDidYouHearAboutUs)
			? answers.howDidYouHearAboutUs
			: [];
		if (referral.length === 0) missing.push("howDidYouHearAboutUs");
	}
	return missing;
}

function computeIsAnswered(
	descriptor: Question,
	answers: OnboardingAnswers,
): boolean {
	if (descriptor.type === "journey-steps") {
		return (
			Array.isArray(answers.journeyCheckedStepIds) &&
			answers.journeyCheckedStepIds.length > 0
		);
	}
	if (descriptor.type === "cycle") {
		return (
			answers.cycleNotRelevant === "true" ||
			(typeof answers.cycleLastPeriodStart === "string" &&
				answers.cycleLastPeriodStart !== "")
		);
	}
	if (descriptor.type === "fertility-status") {
		if (!validateQuestionCompletion(descriptor, answers)) return false;
		// Donor + non_binary/other must pick egg/sperm before advancing — the
		// inline micro-question is required (contract §3); gender female/male
		// derives donorType automatically and needs no extra input.
		const genderEnum = toEnum("gender", String(answers.gender ?? ""));
		const statusEnum = toEnum(
			"fertilityStatus",
			String(answers.fertilityStatus ?? ""),
		);
		if (
			statusEnum === "egg_sperm_donor" &&
			(genderEnum === "non_binary" || genderEnum === "other")
		) {
			return answers.donorType === "egg" || answers.donorType === "sperm";
		}
		return true;
	}
	return validateQuestionCompletion(descriptor, answers);
}
